import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { Filter, OperationState, SearchInfoTarget, Sort, StateGroupFilter, TypesFilterFlags, TimeFilter } from '../../../store/appState';
import styled from 'styled-components/macro';
import Button from '../../shared/Button';
import * as Actions from '../../../store/actions';
import { Link, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { UrlParam } from '../../../constants/urlParams';
import { useQueryParam } from '../../../hooks/routing';
import { SpaceId } from '../../../config/spaces/spaceIds';
import { getSpaceConfig } from '../../../config/serverConfig';
import { SearchBox } from '@fluentui/react/lib/SearchBox';
import FeedbackList from '../MyFeedback/FeedbackList';
import { forMinWidth } from '../../../theme/breakpoints';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { ResourceStrings } from '../../../ResourceStrings';
import { pluralize } from '../../../utils/text';
import { formatNumber } from 'humanize-plus';
import { getSpaceIdFromSlug } from '../../../config/serverConfig';
import { SpaceConfig } from '../../../config/spaces/spaceConfig';
import { SignInState, App } from '../../../app';
import { AppPaths } from '../../../constants/urls';
import { TelemetryResult } from '../../../telemetry/TelemetryHelper';

const defaultPageSize: number = 40;    // @TODO: Update search API to accept larger page sizes, currently getting error "Take value is too large" on passing 50
const defaultSortValue: string = "relevance";

const sortOptions: IDropdownOption[] = [
    { key: Sort.relevance, text: "Relevance" },
    // { key: Sort.active, text: "Hot" },   // @TODO: Hide until we have better logic for hotness
    { key: Sort.newest, text: "Newest" },
    { key: Sort.votes, text: "Most votes" },
    { key: Sort.recent, text: "Recent activity" },
];

const feedbackScopeFilterOptions: IDropdownOption[] = [
    { key: "", text: "All feedback" },
    { key: "follow", text: "My feedback" },
];

export const feedbackStateGroupFilterOptions: IDropdownOption[] = [
    { key: "", text: "All states" },
    { key: "active", text: "Open" },
    { key: "closed", text: "Closed" },
];

export const feedbackTypeFilterOptions: IDropdownOption[] = [
    { key: "", text: "All types" },
    { key: "problem", text: ResourceStrings.problems() },
    { key: "idea", text: "Feature requests" },
];

export const feedbackTimeFilterOptions: IDropdownOption[] = [
    { key: TimeFilter.sixMonths, text: "Past 6 months" },
    { key: TimeFilter.twelveMonths, text: "Past 12 months" },
    { key: TimeFilter.eighteenMonths, text: "Past 18 months" },
    { key: TimeFilter.allTime, text: "All Time" },
];

const paramToFeedbackTypeFlag = (value: string): TypesFilterFlags => {
    switch (value) {
        case "problem": return TypesFilterFlags.problem;
        case "idea": return TypesFilterFlags.idea;
        default: return TypesFilterFlags.all;
    }
}

const paramToFeedbackState = (value: string): (StateGroupFilter | null) => {
    switch (value) {
        case "active": return StateGroupFilter.active;
        case "closed": return StateGroupFilter.closed;
        default: return null;
    }
}

const paramToTimeFilter = (value: string): (number | null) => {
    switch (value) {
        case TimeFilter.sixMonths: return -6;
        case TimeFilter.twelveMonths: return -12;
        case TimeFilter.eighteenMonths: return -18;
        case TimeFilter.allTime: return 0;
        default: return 0;
    }
}

const paramToSortvalue = (value: string): (Sort | null) => {
    switch (value) {
        case Sort.active: return Sort.active;
        case Sort.newest: return Sort.newest;
        case Sort.votes: return Sort.votes;
        case Sort.recent: return Sort.recent;
        default: return null;
    }
}

const parseInt = (value: string, defaultValue: number | null, minValue: number | null): (number | null) => {
    if (!value) {
        return defaultValue;
    }
    const int = Number.parseInt(value);
    if (Number.isNaN(int)) {
        return defaultValue;
    }
    return minValue != null ? Math.max(int, minValue) : int;
}

interface SearchViewRouterParams {
    spaceSlug?: string;
}

export interface SearchViewProps {
}

export default function SearchView(): JSX.Element {
    const history = useHistory();
    const signinState = useSelector((state: RootState) => state.appState.signInState);

    const searchTextParam = useQueryParam(UrlParam.SearchText);
    const [searchText, setSearchText] = useState<string>(searchTextParam);
    const [forceSearchAgain, setForceSearchAgain] = useState(true);  // Needed to force search if user doesn't change any filters or search-text but hits 'Search' button

    const feedbackScopeFilterParam = useQueryParam(UrlParam.FeedbackScopeFilter);
    const feedbackStateGroupFilterParam = useQueryParam(UrlParam.FeedbackStateGroupFilter);
    // const feedbackAreaFilterParam = useQueryParam(UrlParam.FeedbackAreaFilter);  // @TODO: Enable once we have the options list
    const feedbackTypeFilterParam = useQueryParam(UrlParam.FeedbackTypeFilter);
    const feedbackTimeFilterParam = useQueryParam(UrlParam.FeedbackTimeFilter);
    
    const sortParam = useQueryParam(UrlParam.Sort);

    const pageNumberParam = useQueryParam(UrlParam.PageNumber);
    const pageSizeParam = useQueryParam(UrlParam.PageSize);

    const feedbackScopeFilterValue = useMemo(() => signinState === SignInState.Success ? (feedbackScopeFilterParam ?? "") : "", [feedbackScopeFilterParam, signinState]);    // use 'all feedback' option if user isn't signed in
    const feedbackStateGroupFilterValue = useMemo(() => feedbackStateGroupFilterParam ?? "", [feedbackStateGroupFilterParam]);
    const feedbackTypeFilterValue = useMemo(() => feedbackTypeFilterParam ?? "", [feedbackTypeFilterParam]);
    const feedbackTimeFilterValue = useMemo(() => feedbackTimeFilterParam ?? TimeFilter.twelveMonths, [feedbackTimeFilterParam]);
    const sortValue = useMemo(() => sortParam ?? defaultSortValue, [sortParam]);

    const pageNumberValue = useMemo(() => parseInt(pageNumberParam, 0, 0), [pageNumberParam]);
    const pageSizeValue = useMemo(() => parseInt(pageSizeParam, defaultPageSize, 1), [pageSizeParam]);

    const searchResults = useSelector((state: RootState) => state.appState.problems);

    const [searchDescription, setSearchDescription] = useState(false);

    // default to VS if spaceId query param is not specified or invalid
    // nikolami: 2022-10-14: since we default to VS space we will not return null here, 
    // but can in the future if we have combined search. That's why the spaceConfig type is nullable
    const { spaceSlug: spaceSlugParam } = useParams<SearchViewRouterParams>();
    const spaceConfig: SpaceConfig | null = useMemo(() => {
        const spaceIdFromRouter: SpaceId | null = getSpaceIdFromSlug(spaceSlugParam);
        return getSpaceConfig(spaceIdFromRouter ?? SpaceId.VS);
    }, [spaceSlugParam]);

    const filterChanged = (filterName: UrlParam, filterValue: string | number, resetPageNumber: boolean): void => {
        const params = new URLSearchParams(history.location.search);
        if (filterValue == null || filterValue === undefined || filterValue === "") {
            params.delete(filterName);
        } else {
            params.set(filterName, filterValue.toString());
        }
        if (resetPageNumber) {
            params.delete(UrlParam.PageNumber);
        }
        const newParams = params.toString();
        history.push(`${history.location.pathname}?${newParams}`);
        if (filterName === UrlParam.FeedbackTimeFilter && filterValue != null) {
            App.telemetry.postUserTask("TimeFilterChanged", TelemetryResult.Success, undefined, undefined, {
                "TimeChosen": filterValue.toString()
            });
        }
    }

    const onSearchRequested = (): void => {
        App.telemetry.postUserTask("TimeFilterSearchClicked", TelemetryResult.Success, undefined, undefined, {
            "TimeChosen": feedbackTimeFilterValue
        });
        setSearchDescription(false);
        filterChanged(UrlParam.SearchText, searchText, true);
        setForceSearchAgain(x => !x);
    };

    useEffect(
        () => {
            if (signinState === SignInState.Checking || signinState === SignInState.Executing) {
                return;
            }
            const options: Actions.SearchOptions = new Actions.SearchOptions();
            options.updateCountsByType = true;
            options.skip = pageNumberValue * pageSizeValue;
            options.take = pageSizeValue;

            options.spaceIds = spaceConfig?.id ? [spaceConfig.id.toString()] : null;
            options.filter = signinState === SignInState.Success && feedbackScopeFilterValue === "follow" ? Filter.follow : Filter.all;
            options.type = paramToFeedbackTypeFlag(feedbackTypeFilterValue);
            options.stateGroup = paramToFeedbackState(feedbackStateGroupFilterValue);
            options.timeFilter = paramToTimeFilter(feedbackTimeFilterValue);
            options.sort = paramToSortvalue(sortValue);

            options.notAdditive = true;
            Actions.searchFor(searchTextParam ?? "", SearchInfoTarget.all, options, "SearchPage");
        }
        , [signinState,
            forceSearchAgain,
            spaceConfig,
            searchTextParam,
            feedbackScopeFilterValue,
            feedbackStateGroupFilterValue,
            feedbackTypeFilterValue,
            sortValue,
            pageNumberValue,
            pageSizeValue,
            feedbackTimeFilterValue]
    );

    const onSearchBoxKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === "Enter") {
            onSearchRequested();
        }
    }

    const feedbackTypeFlag = paramToFeedbackTypeFlag(feedbackTypeFilterValue);
    // Search API returns counts for both problems and ideas even if just one of the type requested so only pick the one selected here
    const problemsCount = feedbackTypeFlag === TypesFilterFlags.all || feedbackTypeFlag === TypesFilterFlags.problem ? searchResults.results?.countsByType?.problem ?? 0 : 0;
    const ideasCount = feedbackTypeFlag === TypesFilterFlags.all || feedbackTypeFlag === TypesFilterFlags.idea ? searchResults.results?.countsByType?.idea ?? 0 : 0;
    const totalResultsCount = problemsCount + ideasCount;

    const maxPages = totalResultsCount / pageSizeValue;
    const reportAProblem = "Report a Problem";
    const suggestAFeature = "Suggest a Feature";
    const location = useLocation();

    return <Wrapper>
        <Content>
            <div className="top-level-text" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button className="report-button" style={{ fontSize: '1.6rem' }}
                    onClick={() => history.push(location.pathname + AppPaths.report)}> {reportAProblem}</Button>
                <Button className="suggest-button" style={{ fontSize: '1.6rem' }}
                    onClick={() => history.push(location.pathname + AppPaths.suggest)}> {suggestAFeature}</Button>
            </div>
            {spaceConfig.slug === "VisualStudioMac" &&
                <div aria-label="End Of Life Alert" style={{
                    'backgroundColor': '#fcf2a7',
                    'fontStyle': 'italic',
                    'padding': '2rem',
                    'marginBottom': '2rem',
                    'marginTop': '2rem'
                }}>
                    <p style={{ 'fontWeight': 'bold' }}>Important!</p>
                    <p>
                        As of August 31st, 2024, Visual Studio for Mac has been retired, in accordance with Microsoft&#39;s Modern Lifecycle Policy. To learn more about the retirement and for alternate options,<a href="https://learn.microsoft.com/en-us/visualstudio/releases/2022/what-happened-to-vs-for-mac" target="_blank">Visual Studio for Mac Retirement Announcement</a> visit the article.
                    </p></div>}
            {spaceConfig.slug === "office" &&
                <div aria-label="End Of Life Alert" style={{
                    'backgroundColor': '#fcf2a7',
                    'fontStyle': 'italic',
                    'padding': '2rem',
                    'marginBottom': '2rem',
                    'marginTop': '2rem'
                }}>
                    <p style={{ 'fontWeight': 'bold' }}>Important!</p>
                    <p>
                        Please note: ES365 Support has moved to <a href='https://aka.ms/OMRHelp'>https://aka.ms/OMRHelp</a> we are no longer accepting Support tickets or Feature requests via EngCom.
                    </p></div>}
                

            <h1 className="page-title top-level-text">Search existing {spaceConfig.displayName} feedback</h1>

            <SearchInputContainer className="top-level-text">
                <Search>
                    <SearchText
                        autoFocus
                        value={searchText ?? ""}
                        onChange={(e, newValue) => {
                            setSearchText(newValue);
                            setSearchDescription(true);
                        }}
                        placeholder="Search here first for answers, topics and users"
                        iconProps={{ iconName: 'Search' }}
                        onKeyPress={(e) => onSearchBoxKeyPress(e)}
                    />

                    <Button onClick={() => onSearchRequested()}>Search</Button>
                </Search>

                {
                    searchDescription &&
                    <SearchDescription>
                        <div><b>&quot;Search terms&quot;</b> exact phrase search</div>
                        <div><b>[tag]</b> search for tickets with that tag</div>
                        <div><b>user:[User Name]</b> search by author</div>
                    </SearchDescription>
                }

            </SearchInputContainer>

            <FiltersBar className="top-level-text">
                <Filters>
                    <span className="sort-options">
                        <label>Sort by</label>
                        <Dropdown
                            options={sortOptions}
                            selectedKey={sortValue}
                            title="Sort by"
                            disabled={searchResults.state === OperationState.Executing}
                            dropdownWidth={95}
                            onChange={(_evt, item) => filterChanged(UrlParam.Sort, item.key, true)}
                        />
                    </span>
                    <span className="filter-options">
                        <label>Filter by</label>

                        <Dropdown
                            options={feedbackScopeFilterOptions}
                            selectedKey={feedbackScopeFilterValue}
                            title={signinState === SignInState.Success ? "Filter by feedback scope" : "Please sign in to filter by feedback scope"}
                            disabled={searchResults.state === OperationState.Executing || signinState !== SignInState.Success}
                            onChange={(_evt, item) => filterChanged(UrlParam.FeedbackScopeFilter, item.key, true)}
                        />
                        <Dropdown
                            options={feedbackStateGroupFilterOptions}
                            selectedKey={feedbackStateGroupFilterValue}
                            title="Filter by feedback state"
                            disabled={searchResults.state === OperationState.Executing}
                            onChange={(_evt, item) => filterChanged(UrlParam.FeedbackStateGroupFilter, item.key, true)}
                        />
                        <Dropdown
                            selectedKey={feedbackTypeFilterValue}
                            options={feedbackTypeFilterOptions}
                            title="Filter by feedback type"
                            disabled={searchResults.state === OperationState.Executing}
                            onChange={(_evt, item) => filterChanged(UrlParam.FeedbackTypeFilter, item.key, true)}
                        />
                        <label>Created Date</label>
                        <Dropdown
                            selectedKey={feedbackTimeFilterValue}
                            options={feedbackTimeFilterOptions}
                            title="Filter by time"
                            disabled={searchResults.state === OperationState.Executing}
                            onChange={(_evt, item) => filterChanged(UrlParam.FeedbackTimeFilter, item.key, true)}
                        />
                    </span>
                </Filters>

                <span>
                    {searchResults.state === OperationState.Success && <strong>{formatNumber(totalResultsCount)} {pluralize(totalResultsCount, "item", "items")}</strong>}
                    {searchResults.state !== OperationState.Success && <strong>&nbsp;</strong>}
                </span>
            </FiltersBar>

            <div>
                <FeedbackList feedbackList={searchResults} clearResultsWhenfetching />
            </div>

            <Pager style={{ display: searchResults.state === OperationState.Success && totalResultsCount > 0 ? "flex" : "none" }}>
                <Button
                    buttonType="link"
                    disabled={pageNumberValue <= 0}
                    onClick={() => filterChanged(UrlParam.PageNumber, pageNumberValue - 1, false)}
                >
                    &lt; Previous page
                </Button>
                <Button
                    buttonType="link"
                    disabled={pageNumberValue >= maxPages - 1}
                    onClick={() => filterChanged(UrlParam.PageNumber, pageNumberValue + 1, false)}
                >
                    Next page &gt;
                </Button>
            </Pager>
        </Content>
    </Wrapper>
}

const Pager = styled.div`
    display: flex;
    justify-content: space-between;
    padding-top: 2rem;

    button {
        padding: 0 1.6rem;
        font-size: 1.6rem;
    }
`;

const SearchInputContainer = styled.div`
    margin-bottom: 2rem;
    button {
        padding: 0 1.6rem;
        font-size: 1.6rem;
    }
`;

const SearchText = styled(SearchBox)`
    height: auto;
    border: solid 1px var(--color-surface-dark2);
    margin-right: 1.8rem;
    flex-grow: 1;

    .ms-SearchBox-field {
        font-weight: normal;
        font-size: 1.4rem;
    }

    .ms-SearchBox-field::placeholder {
        font-weight: 350;
        font-size: 1.4rem;
    }

    .ms-SearchBox-icon {
        color: var(--color-on-surface);
    }

    input {
        padding: 0 .6rem;
        border: none;
        font-size: 1.4rem;
    }
`;

const FiltersBar = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 1.6rem;

    >* + * {
        margin-top: 1rem;
    }

    ${forMinWidth.md`
        flex-direction: row;

        >* + * {
            margin-top: 0;
        }
    `}

    label {
        white-space: nowrap;
        font-weight: 600;
    }
`;

const Filters = styled.span`
    display: flex;
    align-items: baseline;
    flex-direction: column;
    font-size: 1.4rem;

    >* + * {
        margin-top: .8rem;
    }

    ${forMinWidth.md`
        flex-direction: row;

        >* + * {
            margin-top: 0;
        }
    `}

    .filter-options {
        display: flex;
        align-items: baseline;
        flex-direction: row;
        flex-wrap: wrap;

        >* + * {
            margin-left: 2rem;
        }

        ${forMinWidth.md`
            margin-left: 4.5rem;
        `}
    }

    .sort-options {
        display: flex;
        align-items: baseline;

        >* + * {
            margin-left: 2rem;
        }
    }

    .ms-Dropdown-title {
        background-color: transparent;
        border: none
    }

    .ms-Dropdown.is-disabled .ms-Dropdown-caretDown,
    .ms-Dropdown.is-disabled .ms-Dropdown-title {
        color: var(--color-on-background-lighter);
        forced-color-adjust: inherit;
    }
`;

const Wrapper = styled.div`
    background-color: var(--color-background);
`;

const Content = styled.main`
    min-height: 60vh;
    margin-left: auto;
    margin-right: auto;
    padding: 3.5rem var(--page-margin-horizontal);

    >* + * {
        margin-top: 0 rem;
    }

    ${SearchInputContainer} + ${FiltersBar} {
        margin-top: 6rem;
    }

    .page-title {
        font-size: 2.4rem;
        font-weight: 600;
        margin-bottom: 2.4rem;
    }

    .report-button{
        margin-right: 0.8rem;
    }
    .suggest-button{
        margin-left: 0.8rem;
    }
`;

const Search = styled.div`
    display:flex;
    width:100%;
`;

const SearchDescription = styled.div`
    background: white;
    font-size: 1.4rem;
    box-shadow: rgba(0, 0, 0, 0.19) 0px 10px 20px, rgba(0, 0, 0, 0.23) 0px 6px 6px;
    width: max-content;
    div{
        padding: 5px;
    }
`;